import { Checkbox, Radio, RadioGroup } from "@mui/material";
import type { ModalComponentProps } from "../../../types/modal.types";
import ConfirmModal from "../confirm.modal";
import Button from "../../ui/button";
import { useEffect, useState } from "react";
import { cn } from "../../../utils/cn.utils";
import { loadTossPayments, type TossPaymentsPayment } from "@tosspayments/tosspayments-sdk";
import { useMutation } from "@tanstack/react-query";
import { postPurchase } from "../../../lib/apis/v1/purchase.service";
import type { PurchaseRequest } from "../../../lib/apis/dtos/request/purchase.request";
import FailSnackbar from "../../ui/snackbar/fail.component";
import type { PurchaseResponse } from "../../../lib/apis/dtos/response/purchase.response";

const PAYMENT_ITEMS = [
  {
    id: 0,
    title: "1개월권",
    month: 1
  },
  // {
  //   id: 1,
  //   title: "180일권",
  //   month: 6
  // },
]

const clientKey = "test_ck_6bJXmgo28e20pDOJ7qe63LAnGKWx";
const customerKey = generateRandomString();

interface PaymentModalProps extends ModalComponentProps {
  pathname: string;
}


export default function PaymentDetailModal({ id, onClose, pathname }: PaymentModalProps) {
  const [payment, setPayment] = useState<TossPaymentsPayment | null>(null);

  const [selectedBranches, setSelectedBranches] = useState<number[]>([]);
  const [selectItems, setSelectItems] = useState<number>();
  const [isAgree, setIsAgree] = useState<boolean>(false);


  useEffect(() => {
    async function fetchPayment() {
      try {
        const tossPayments = await loadTossPayments(clientKey);
        const paymentLoad = tossPayments.payment({ customerKey });

        setPayment(paymentLoad);
      } catch (error) {
        console.error(error);
      }
    }
    fetchPayment();
  }, [clientKey, customerKey])

  async function requestPayment(form: PurchaseResponse) {
    await payment?.requestPayment({
      method: "VIRTUAL_ACCOUNT", // 가상계좌 결제
      amount: {
        value: form.totalPrice,
        currency: "KRW",
      },
      orderId: form.orderId,
      orderName: form.orderName,
      successUrl: window.location.origin + "/payment/success?pathname=" + pathname,
      failUrl: window.location.origin + "/fail",
      customerEmail: "customer123@gmail.com",
      customerName: form.customerName,
      // 가상계좌 안내, 퀵계좌이체 휴대폰 번호 자동 완성에 사용되는 값입니다. 필요하다면 주석을 해제해 주세요.
      // customerMobilePhone: "01012341234",
      virtualAccount: {
        cashReceipt: {
          type: "소득공제",
        },
        useEscrow: false,
        validHours: 24,
      },
    })
  }

  const purchaseOrderMutation = useMutation({
    mutationFn: (form: PurchaseRequest) => postPurchase(form),
    onSuccess: (res) => {
      const { error, result, message } = res;
      if (error) {
        FailSnackbar(`결제 실패 사유(${message})`)
      } else {
        requestPayment(result);
      }
    }
  })

  return (
    <ConfirmModal className="w-[1200px] px-6" id={id} onClose={onClose} title={"월 사용요금 결제"} rightButton={{
      title: "결제하기",
      onClick: () => {
        purchaseOrderMutation.mutate({
          stores: selectedBranches,
          totalPrice: (selectedBranches.length * 220000 * PAYMENT_ITEMS[selectItems || 0].month) - (selectItems === 0 ? 0 : 220000),
        })
      }
    }}>
      <div className="flex-row gap-10 py-4 items-start">
        <div className="flex-1">
          <p className="text-style-title-03 pb-3 border-b border-gray-20">결제할 지점</p>
          <table className="w-full mt-4 rounded-[8px] border border-gray-20">
            <thead className="">
              <tr>
                <th className="bg-gray-02">
                  <div className="flex-row-between px-4 py-2">
                    <div className="flex-row">
                      <Checkbox aria-label="전체 선택" checked={selectedBranches.length === 5} onChange={() => setSelectedBranches((prev) => {
                        if (prev.length === 5) {
                          return [];
                        } else {
                          return [559, 560, 561, 562, 563];
                        }
                      })} />
                      <p className="font-normal">전체 선택</p>
                    </div>
                    {selectedBranches.length > 0 && (
                      <p className="text-style-body-04 text-primary-50">{selectedBranches.length}개 지점 선택됨</p>
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5, }).map((_, index) => (
                <tr key={index}>
                  <td>
                    <div className="flex-row-between px-4 py-2 border-b border-gray-20 last:border-b-0">
                      <div className="flex-row ">
                        <Checkbox aria-label="너텔레콤 강남점 A" checked={selectedBranches.includes(index + 559)} onChange={() => setSelectedBranches((prev) => {
                          if (prev.includes(index + 559)) {
                            return prev.filter((item) => item !== index + 559);
                          } else {
                            return [...prev, index + 559];
                          }
                        })} />
                        <p>너텔레콤 강남점 A</p>
                        <Button
                          variant={true ? "primary" : "gray"}
                          label={true ? "활성" : "비활성"}
                          className="px-[10px] h-6 rounded-[100px] text-[13px] ml-2"
                        />
                      </div>
                      <div className="flex-row justify-end gap-4 text-style-body-04">
                        <p>~2025-09-09 (3일 남음)</p>
                        <p className="font-normal text-gray-50">220,000원</p>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex-row pb-3 border-b border-gray-200 gap-[10px] mt-10" >
            <p className="text-style-title-03">상품 선택</p>
            <span className="text-style-body-03 font-normal text-red-500">결제할 지점을 먼저 선택해주세요.</span>
          </div>
          <div className="pt-4">
            <RadioGroup className="space-y-[8px]">
              {PAYMENT_ITEMS.map(({ month, title }, index) => (
                <div key={index} className="flex-row-between border border-gray-10 rounded-[8px] p-4">
                  <div className="flex-row gap-2">
                    <Radio disabled={selectedBranches.length === 0} checked={selectItems === index} onChange={() => setSelectItems(index)} />
                    <p className={cn("text-style-body-04", selectedBranches.length > 0 ? "text-gray-50" : "text-gray-20")}>{title}</p>
                  </div>
                  <div>
                    {selectedBranches.length > 0 && (
                      <>
                        {index === 1 && <p className="text-style-body-04 font-normal text-gray-50 line-through text-right">{(selectedBranches.length * 220000 * month).toLocaleString('ko-KR')}원</p>}
                        <p className="text-style-body-04">{((selectedBranches.length * 220000 * month) - (index === 0 ? 0 : 220000)).toLocaleString('ko-KR')}원</p>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </RadioGroup>
            <p className="text-style-body-03 font-normal text-gray-80 mt-2">*사용기간 예시) 1월 15일 결제 {">"} 2월 14일까지 사용</p>
          </div>
        </div>
        <div className="w-[312px]">
          <p className="text-style-title-03 pb-3 border-b border-gray-20">최종 결제 금액</p>
          {selectedBranches.length > 0 && selectItems !== null && (
            <div className="flex-row-between py-4 text-style-body-04 text-gray-80 border-b border-gray-20">
              <p className="font-normal">{selectedBranches.length}개 지점</p>
              <p>{selectItems !== undefined ? ((selectedBranches.length * 220000 * PAYMENT_ITEMS[selectItems || 0].month)).toLocaleString('ko-KR') : "0"}원</p>
            </div>
          )}
          <div className="flex-row-between py-4 text-style-body-05 text-primary-50">
            <p className="font-normal">총 결제 예정 금액</p>
            <p>{selectItems !== undefined ? ((selectedBranches.length * 220000 * PAYMENT_ITEMS[selectItems || 0].month)).toLocaleString('ko-KR') : "0"}원</p>
          </div>
          <div className="mt-6 bg-gray-02 px-4 py-3 rounded-[8px]">
            <div>
              <Checkbox checked={isAgree} onChange={() => setIsAgree((prev) => !prev)} />
              <span className="text-style-title-01">구매조건 및 결제진행 동의</span>
            </div>
            <div className="flex-row items-start text-style-title-01 font-normal mt-4">
              <span className="px-2">∙</span>
              <span>선택한 지점들에 대해 확인했으며 결제 진행에 동의하시겠습니까?</span>
            </div>
            <a href="https://kendrickpark.notion.site/payment" target="_blank">
              <p className="mt-3 text-style-title-01 font-normal text-primary-50">결제 및 환불약관 (전문)</p>
            </a>
          </div>
          {!isAgree && (<p className="text-style-body-03 font-normal text-red-50 mt-2">구매조건 및 결재진행 동의해주세요</p>)}
        </div>
      </div>
    </ConfirmModal>
  )
}

function generateRandomString() {
  return window.btoa(Math.random().toString()).slice(0, 20);
}